import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Table, Header, Icon, Label } from 'semantic-ui-react';
import { formatCurrencyRupiah } from 'utils/currencyUtils';
import LoadingSpinner from 'components/LoadingSpinner/LoadingSpinner';
import ApprovalRequirements from 'pages/BillingPackage/components/ApprovalRequirements';
import CustomPackageFilter, {
  DEFAULT_FILTERS,
} from 'pages/BillingPackage/components/CustomPackageFilter';
import { TAX_RATE } from 'constants/billingPackage/calculationConstants';
import { PLATFORM_LABELS } from 'constants/billingPackage/platformConstants';

const getModuleColor = module => {
  const colorMap = {
    Basic: 'blue',
    Professional: 'purple',
    Enterprise: 'orange',
  };
  return colorMap[module] || 'grey';
};

const getPlatformColor = platform => {
  const colorMap = {
    MOKA_BACKOFFICE: 'blue',
    INTERNAL_TOOL: 'green',
  };
  return colorMap[platform] || 'grey';
};

function roundHalfUp(num) {
  return Math.sign(num) * Math.round(Math.abs(num));
}

function calculateFinalPrice(subtotal, discount, tax) {
  const discountAmount = (subtotal * discount) / 100;
  const totalPrice = subtotal - discountAmount;
  const taxAmount = (totalPrice * tax) / 100;
  return roundHalfUp(totalPrice + taxAmount);
}

const CustomPackageTable = ({
  packages,
  isLoading,
  onEdit,
  onDelete,
  title,
  isUpgradeTable,
}) => {
  const upgradeColSpan = '4';
  const colSpan = '8';

  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);

  const filteredPackages = useMemo(() => {
    if (isUpgradeTable) return packages;

    return packages.filter(pkg => {
      if (
        appliedFilters.baseModule !== 'all' &&
        pkg.subscription_product.name !== appliedFilters.baseModule
      ) {
        return false;
      }

      if (
        appliedFilters.duration !== 'all' &&
        pkg.quantity !== appliedFilters.duration
      ) {
        return false;
      }

      if (appliedFilters.approval !== 'all') {
        const requiresApproval =
          pkg.approval_requirements && pkg.approval_requirements.length > 0;

        if (appliedFilters.approval === 'yes' && !requiresApproval) {
          return false;
        }
        if (appliedFilters.approval === 'no' && requiresApproval) {
          return false;
        }
      }

      if (
        appliedFilters.platform !== 'all' &&
        !(pkg.available_platforms || []).includes(appliedFilters.platform)
      ) {
        return false;
      }

      return true;
    });
  }, [packages, appliedFilters, isUpgradeTable]);

  const renderUpgradeTableContent = () => {
    if (isLoading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={upgradeColSpan}>
            <LoadingSpinner />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (packages.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={upgradeColSpan} style={{ textAlign: 'center' }}>
            No data
          </Table.Cell>
        </Table.Row>
      );
    }

    return packages.map(pkg => (
      <Table.Row key={pkg.id}>
        <Table.Cell
          style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={pkg.name}
        >
          {pkg.name}
        </Table.Cell>
        <Table.Cell>{formatCurrencyRupiah(pkg.total_price)}</Table.Cell>
        <Table.Cell>{pkg.discount_rule.discount_percentage}%</Table.Cell>
        <Table.Cell textAlign="center">
          <Icon
            name="pencil"
            link
            color="grey"
            onClick={() => onEdit(pkg, false)}
            style={{ cursor: 'pointer' }}
          />
        </Table.Cell>
      </Table.Row>
    ));
  };

  const renderTableContent = () => {
    if (isLoading) {
      return (
        <Table.Row>
          <Table.Cell colSpan={colSpan}>
            <LoadingSpinner />
          </Table.Cell>
        </Table.Row>
      );
    }

    if (filteredPackages.length === 0) {
      return (
        <Table.Row>
          <Table.Cell colSpan={colSpan} style={{ textAlign: 'center' }}>
            No data
          </Table.Cell>
        </Table.Row>
      );
    }

    return filteredPackages.map(pkg => (
      <Table.Row key={pkg.id}>
        <Table.Cell
          style={{
            maxWidth: '200px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={pkg.name}
        >
          {pkg.name}
        </Table.Cell>
        <Table.Cell>
          <Label color={getModuleColor(pkg.subscription_product.name)}>
            {pkg.subscription_product.name}
          </Label>
        </Table.Cell>
        <Table.Cell>{pkg.quantity} month(s)</Table.Cell>
        <Table.Cell>{pkg.discount_rule.discount_percentage}%</Table.Cell>
        <Table.Cell>{formatCurrencyRupiah(pkg.total_price)}</Table.Cell>
        <Table.Cell>
          {formatCurrencyRupiah(
            calculateFinalPrice(
              pkg.total_price,
              pkg.discount_rule.discount_percentage,
              TAX_RATE
            )
          )}
        </Table.Cell>
        <Table.Cell textAlign="center">
          <ApprovalRequirements requirements={pkg.approval_requirements} />
        </Table.Cell>
        <Table.Cell>
          {pkg.available_platforms && pkg.available_platforms.length > 0
            ? pkg.available_platforms.map(platform => (
                <Label
                  key={platform}
                  color={getPlatformColor(platform)}
                  style={{ marginBottom: '0.2em' }}
                >
                  {PLATFORM_LABELS[platform] || platform}
                </Label>
              ))
            : '-'}
        </Table.Cell>
        <Table.Cell textAlign="center">
          <Icon
            name="pencil"
            link
            color="blue"
            onClick={() => onEdit(pkg, false)}
            style={{ marginRight: '1em', cursor: 'pointer' }}
          />
          <Icon
            name="trash"
            link
            color="red"
            onClick={() => onDelete(pkg, false)}
            style={{ cursor: 'pointer' }}
          />
        </Table.Cell>
      </Table.Row>
    ));
  };

  if (isUpgradeTable) {
    return (
      <div>
        <Header as="h3">{title}</Header>
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Upgrade Path</Table.HeaderCell>
              <Table.HeaderCell>Price Delta</Table.HeaderCell>
              <Table.HeaderCell>Discount Threshold</Table.HeaderCell>
              <Table.HeaderCell textAlign="center" width={2}>
                Action
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>{renderUpgradeTableContent()}</Table.Body>
        </Table>
      </div>
    );
  }

  return (
    <div>
      <Header as="h3">{title}</Header>
      <CustomPackageFilter
        packages={packages}
        onApply={setAppliedFilters}
        onReset={() => setAppliedFilters(DEFAULT_FILTERS)}
      />
      <Table>
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell>Package Name</Table.HeaderCell>
            <Table.HeaderCell>Base Module</Table.HeaderCell>
            <Table.HeaderCell>Duration</Table.HeaderCell>
            <Table.HeaderCell>Discount</Table.HeaderCell>
            <Table.HeaderCell>Price</Table.HeaderCell>
            <Table.HeaderCell>Final Price</Table.HeaderCell>
            <Table.HeaderCell textAlign="center" width={2}>
              Approval
            </Table.HeaderCell>
            <Table.HeaderCell>Platform</Table.HeaderCell>
            <Table.HeaderCell textAlign="center" width={2}>
              Action
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{renderTableContent()}</Table.Body>
      </Table>
    </div>
  );
};

CustomPackageTable.propTypes = {
  packages: PropTypes.array,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  title: PropTypes.string,
  isUpgradeTable: PropTypes.bool,
};

CustomPackageTable.defaultProps = {
  packages: [],
  isLoading: false,
  title: 'Customize Package',
  isUpgradeTable: false,
  onDelete: () => {},
};

export default CustomPackageTable;
